/**
 * 管理后台 · 审计日志（见方案 2B.3 A/B 类）。
 *
 * 可见范围刻意做了裁剪：
 *   - 任何动作都能看到：时间、操作者、动作名、目标类型/id、IP
 *   - **只有 admin.* 动作**才显示操作前后值
 *
 * 原因：家长动作的 before/after 里可能包含学生昵称、打卡备注这类家庭内容，
 * 管理员看一眼日志就等于绕过了"不能看家庭数据"的边界（见方案 2B.1）。
 * 后端在返回时就已经把非 admin 动作的 before/after 置空，前端这里只是照着显示。
 */
import { Fragment, useMemo, useState } from 'react';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { AdminShell } from '../../components/layout/AdminShell';
import { useAdminAudit } from '../../lib/queries';
import { ADMIN_ACTIONS } from '@fht/shared';
import { toErrorMessage } from '../../lib/api/error';
import { formatDateTimeFull } from '../../lib/utils';
import type { AdminAuditRow } from '../../lib/api/admin';

const PAGE_SIZE = 30;

const ACTOR_LABEL: Record<AdminAuditRow['actorType'], string> = {
  parent: '家长',
  student: '学生',
  system: '系统 / 管理员',
};

/**
 * 常见动作的中文说明（查不到就显示原始 action，便于排查）。
 *
 * ⚠️ `admin.user.reset_password`（网页重置）已于 2026-10-20 移除，这里换成
 *    `admin.user.reset_password_cli` —— 重置密码这个动作还在，但只发生在
 *    服务器命令行上（`actor_type = system`），所以要标清"命令行紧急恢复"，
 *    否则管理员在审计里看到它会以为网页上还有这条路。
 */
const ACTION_LABEL: Record<string, string> = {
  'auth.login': '登录',
  'auth.password.change': '修改密码',
  'auth.password.reset.request': '请求重置密码（自助）',
  'auth.password.reset.success': '自助重置成功',
  'auth.password.reset.failed': '自助重置失败',
  'auth.totp.enable': '开启动态口令',
  'admin.user.reset_password_cli': '重置账号密码（命令行紧急恢复）',
  'admin.user.revoke_sessions': '强制退出全部设备',
  'admin.user.disable': '停用账号',
  'admin.user.enable': '启用账号',
  'admin.settings.update': '修改系统设置',
  'admin.invite_code.create': '生成邀请码',
  'admin.mail_test.send': '邮件自检',
  'parent.register': '家长注册',
  'student.create': '创建学生',
  'student.password.reset': '重置孩子密码（家长操作）',
};

function JsonBlock({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-slate-400">-</span>;
  return (
    <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-slate-50 p-2 text-[11px] text-slate-700">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export function AdminAuditPage() {
  const [action, setAction] = useState('');
  const [actorId, setActorId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      ...(action.trim() ? { action: action.trim() } : {}),
      ...(actorId.trim() ? { actorId: actorId.trim() } : {}),
      ...(from ? { from: new Date(from).toISOString() } : {}),
      ...(to ? { to: new Date(to).toISOString() } : {}),
    }),
    [page, action, actorId, from, to],
  );

  const auditQuery = useAdminAudit(query);
  const rows = auditQuery.data?.rows ?? [];
  const total = auditQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AdminShell
      title="审计日志"
      description={`共 ${total} 条。所有管理动作都留痕；家长操作只显示"谁在什么时候做了什么"，不显示内容。`}
    >
      <Card className="mb-4">
        <CardHeader
          title="过滤"
          description="动作名支持前缀筛选，例如 admin. 只看管理动作、auth. 只看登录相关"
        />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            label="动作"
            placeholder="如 admin.user."
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
          />
          <Input
            label="操作者 ID"
            placeholder="auth_user.id（UUID）"
            value={actorId}
            onChange={(e) => {
              setActorId(e.target.value);
              setPage(1);
            }}
          />
          <Input
            label="起始时间"
            type="datetime-local"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
          />
          <Input
            label="结束时间"
            type="datetime-local"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setAction('admin.');
              setPage(1);
            }}
          >
            只看管理动作
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setAction('auth.login');
              setPage(1);
            }}
          >
            只看登录
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setAction('');
              setActorId('');
              setFrom('');
              setTo('');
              setPage(1);
            }}
          >
            清空过滤
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-400">
          可审计的管理动作白名单：{ADMIN_ACTIONS.join('、')}
        </p>
      </Card>

      {auditQuery.isLoading && <Skeleton className="h-64" />}

      {auditQuery.isError && (
        <Card>
          <p role="alert" className="text-sm text-rose-700">
            读取审计日志失败：{toErrorMessage(auditQuery.error)}
          </p>
        </Card>
      )}

      {!auditQuery.isLoading && rows.length === 0 && (
        <EmptyState icon="🧾" title="没有匹配的日志" description="换一个动作名或时间范围试试。" />
      )}

      {rows.length > 0 && (
        <Card className="overflow-x-auto p-0">
          <table className="w-full min-w-[52rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">时间</th>
                <th className="px-4 py-3">操作者</th>
                <th className="px-4 py-3">动作</th>
                <th className="px-4 py-3">目标</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3 text-right">前后值</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const hasPayload = row.before !== null || row.after !== null;
                return (
                  <Fragment key={row.id}>
                    <tr className="border-b border-slate-100 last:border-0">
                      {/* 审计时间是"哪一天发生的"，必须带年份，理由同账号页。 */}
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                        {formatDateTimeFull(row.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          tone={
                            row.actorType === 'system'
                              ? 'brand'
                              : row.actorType === 'parent'
                                ? 'neutral'
                                : 'warn'
                          }
                        >
                          {ACTOR_LABEL[row.actorType]}
                        </Badge>
                        <p className="mt-1 text-[11px] text-slate-400">{row.actorId ?? '-'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">
                          {ACTION_LABEL[row.action] ?? row.action}
                        </p>
                        <p className="text-[11px] text-slate-400">{row.action}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {row.targetType ?? '-'}
                        <br />
                        {row.targetId ?? ''}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{row.ip ?? '-'}</td>
                      <td className="px-4 py-3 text-right">
                        {hasPayload ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                          >
                            {expanded === row.id ? '收起' : '查看'}
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">
                            {row.actorType === 'system' ? '-' : '（家庭操作，不展示内容）'}
                          </span>
                        )}
                      </td>
                    </tr>
                    {expanded === row.id && hasPayload && (
                      <tr className="border-b border-slate-100 bg-slate-50/60">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <p className="mb-1 text-xs font-medium text-slate-500">操作前</p>
                              <JsonBlock value={row.before} />
                            </div>
                            <div>
                              <p className="mb-1 text-xs font-medium text-slate-500">操作后</p>
                              <JsonBlock value={row.after} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
          <span>
            第 {page} / {totalPages} 页
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              上一页
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
